import { DATA } from '@/data'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import AboutHeadshot from '@/components/AboutHeadshot'
import Glitch from '@/components/Glitch'
import ImageReveal from '@/components/ImageReveal'
import TerminalCoursework from '@/components/TerminalCoursework'
import SEO from '@/components/SEO'

function FadeSection({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            className={className}
        >
            {children}
        </motion.div>
    )
}

function Cmd({ command }: { command: string }) {
    return (
        <p className="font-mono text-sm mb-5">
            <span className="text-primary font-bold">$</span>{' '}
            <span className="text-foreground">{command}</span>
        </p>
    )
}

function AsciiBar({ label, value, max = 10 }: { label: string; value: number; max?: number }) {
    const filled = Math.round(value)
    const empty = max - filled
    return (
        <div className="flex items-center gap-3 font-mono text-xs">
            <span className="w-28 text-[#888] truncate">{label}</span>
            <span className="text-primary">{'█'.repeat(filled)}</span>
            <span className="text-[#222]">{'█'.repeat(empty)}</span>
        </div>
    )
}

const skills = [
    { category: 'Frontend',     items: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Framer Motion'] },
    { category: 'Backend',      items: ['PHP', 'MySQL', 'REST APIs', 'Python'] },
    { category: 'Tools/Cloud',  items: ['Git', 'GitHub', 'AWS', 'Vercel', 'Vite'] },
    { category: 'Other',        items: ['STEM Education', 'Technical Leadership', 'Robotics', 'Agile'] },
]

const proficiency = [
    { label: 'Python',     value: 9 },
    { label: 'TypeScript', value: 8 },
    { label: 'React',      value: 8 },
    { label: 'Next.js',    value: 7 },
    { label: 'PHP',        value: 5 },
    { label: 'MySQL',      value: 6 },
]

/* Hex ID per experience */
const HEX_IDS = ['0x4F2A', '0x9D1C', '0xB3E8', '0x2A7F', '0xC491', '0x7D0E']

export default function AboutPage() {
    return (
        <div className="min-h-screen font-mono py-12">
            <SEO
                title="About | Rapheal Kwabena Asomani Gadoh — Background & Experience"
                description="Background, experience, education, skills, and certifications of Rapheal Kwabena Asomani Gadoh — Full Stack Engineer and STEM Educator."
                canonical="https://raphealgadoh.me/about"
            />
            <div className="max-w-6xl mx-auto px-6 space-y-20">

                {/* ── cat about.md — headshot + terminal window ── */}
                <FadeSection>
                    <Cmd command="cat about.md" />

                    {/* On mobile: headshot above; on desktop: side-by-side */}
                    <div className="flex flex-col lg:flex-row gap-10 lg:gap-12 items-start">

                        {/* Left: terminal window */}
                        <div className="terminal-window flex-1">
                            <div className="terminal-titlebar">
                                <span className="terminal-dot bg-[#ff5f57]" />
                                <span className="terminal-dot bg-[#ffbd2e]" />
                                <span className="terminal-dot bg-[#28c840]" />
                                <span className="ml-3 text-[10px] text-[#444]">about.md</span>
                            </div>
                            <div className="p-6 space-y-4 text-sm">
                                <p className="text-foreground">{DATA.summary}</p>
                                <p className="text-[#888] text-xs leading-relaxed border-l border-[#222] pl-3">
                                    {DATA.appreciation}
                                </p>
                                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs pt-2">
                                    <span className="text-[#555]">Location:</span>
                                    <span className="text-foreground">United States 🇺🇸</span>
                                    <span className="text-[#555]">Portfolio:</span>
                                    <span className="text-foreground">raphealgadoh.me</span>
                                    <span className="text-[#555]">Status:</span>
                                    <span className="flex items-center gap-1.5 text-primary">
                                        <span className="status-dot-online" />
                                        <Glitch text="Available" trigger="ambient" interval={[10000, 18000]}>
                                            Available
                                        </Glitch>
                                    </span>
                                    <span className="text-[#555]">Open to:</span>
                                    <span className="text-foreground">Freelance &amp; Full-time</span>
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <a href="/Rapheal-Kwabena-Resume.pdf" download className="btn-term-accent text-xs">
                                        <Glitch text="[ resume.pdf ]" trigger="hover">[ resume.pdf ]</Glitch>
                                    </a>
                                    <Link to="/contact" className="btn-term text-xs">
                                        <Glitch text="[ get in touch ]" trigger="hover">[ get in touch ]</Glitch>
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Right: terminal-framed headshot */}
                        <div className="flex flex-col items-center lg:items-start shrink-0">
                            <p className="text-[10px] text-[#444] font-mono mb-2">// identity.sys</p>
                            <AboutHeadshot />
                        </div>

                    </div>
                </FadeSection>

                {/* ── skills ── */}
                <FadeSection>
                    <Cmd command="skills --all" />
                    <div className="grid md:grid-cols-2 gap-12">
                        {/* Proficiency bars */}
                        <div>
                            <p className="text-xs text-[#555] mb-4">// core proficiency</p>
                            <div className="space-y-3">
                                {proficiency.map(s => <AsciiBar key={s.label} label={s.label} value={s.value} />)}
                            </div>
                        </div>

                        {/* Category lists */}
                        <div className="space-y-6">
                            {skills.map(cat => (
                                <div key={cat.category}>
                                    <p className="text-xs text-primary mb-2 font-bold">{cat.category}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {cat.items.map(item => (
                                            <span key={item} className="text-[10px] px-2 py-0.5 border border-[#222] text-[#666] rounded-sm">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </FadeSection>

                {/* ── work experience — Personnel Records ── */}
                <FadeSection>
                    <Cmd command="cat experience.log" />
                    <div className="space-y-6 max-w-3xl">
                        {DATA.work.map((role, idx) => (
                            <motion.div
                                key={role.id}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.07 }}
                                className="dossier-card group"
                            >
                                {/* Header bar */}
                                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#111] bg-[#040404]">
                                    <span className="text-[9px] font-mono text-primary/60 tracking-widest">PERSONNEL RECORD — VERIFIED</span>
                                    <span className="text-[9px] font-mono text-[#333] group-hover:text-primary/50 transition-colors">
                                        {HEX_IDS[idx % HEX_IDS.length]}
                                    </span>
                                </div>

                                <div className="p-4 flex gap-5">
                                    {/* Experience image */}
                                    <div className="shrink-0">
                                        <ImageReveal
                                            src={role.logoUrl}
                                            alt={role.company}
                                            className="w-14 h-14 object-contain"
                                            wrapperClassName="w-14 h-14"
                                        />
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1 min-w-0">
                                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[10px] mb-2">
                                            <span className="text-[#444]">Position:</span>
                                            <span className="text-primary font-semibold truncate">{role.title}</span>
                                            <span className="text-[#444]">Organization:</span>
                                            <span className="text-foreground truncate">{role.company}</span>
                                            <span className="text-[#444]">Duration:</span>
                                            <span className="text-[#888]">{role.start} – {role.end}</span>
                                            <span className="text-[#444]">Status:</span>
                                            <span className="text-primary flex items-center gap-1">
                                                <span className="status-dot-online" /> VERIFIED
                                            </span>
                                        </div>

                                        <p className="text-[10px] text-[#444] mb-1">Mission Summary:</p>
                                        <p className="text-[10px] text-[#666] leading-relaxed border-l border-[#1a1a1a] pl-2">
                                            {role.description}
                                        </p>

                                        <div className="mt-2 pt-2 border-t border-[#111] flex items-center justify-between">
                                            <span className="text-[9px] font-mono text-[#333]">ACCESS LEVEL</span>
                                            <span className="text-[9px] font-mono text-primary/70">AUTHORIZED</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </FadeSection>

                {/* ── education ── */}
                <FadeSection>
                    <Cmd command="cat education.log" />
                    <div className="space-y-4 max-w-3xl">
                        {DATA.education.map((edu) => (
                            <div key={edu.id} className="term-card">
                                <div className="flex gap-4">
                                    {/* Education image */}
                                    <div className="shrink-0">
                                        <ImageReveal
                                            src={edu.logoUrl}
                                            alt={edu.school}
                                            className="w-12 h-12 object-contain"
                                            wrapperClassName="w-12 h-12"
                                        />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-primary font-bold mb-0.5">{edu.school}</p>
                                        <p className="text-xs text-foreground mb-0.5">{edu.degree}</p>
                                        <p className="text-[10px] text-[#555]">{edu.start} – {edu.end}</p>
                                        {edu.description && (
                                            <p className="text-[10px] text-[#666] mt-1 leading-relaxed">{edu.description}</p>
                                        )}

                                        {/* Terminal coursework only for UAPB */}
                                        {'coursework' in edu && edu.coursework && (
                                            <TerminalCoursework />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </FadeSection>

                {/* ── certifications ── */}
                <FadeSection>
                    <Cmd command="ls certifications/" />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {DATA.certifications.map((cert) => (
                            <div key={cert.id} className="term-card text-xs">
                                <p className="text-primary font-bold mb-1">{cert.organization}</p>
                                <p className="text-foreground mb-2 leading-tight">{cert.title}</p>
                                <p className="text-[#666] text-[10px] leading-relaxed">{cert.description}</p>
                            </div>
                        ))}
                    </div>
                </FadeSection>

                {/* ── awards ── */}
                <FadeSection>
                    <Cmd command="cat awards.log" />
                    <div className="space-y-0 max-w-2xl">
                        {DATA.hackathons.map((award) => (
                            <div key={award.id} className="achievement-card flex gap-4 mb-3 group">
                                {/* Award image */}
                                <div className="shrink-0">
                                    <ImageReveal
                                        src={award.logo}
                                        alt={award.title}
                                        className="w-10 h-10 object-contain"
                                        wrapperClassName="w-10 h-10"
                                    />
                                </div>

                                <div className="flex-1 text-xs">
                                    <p className="text-foreground font-semibold">{award.title}</p>
                                    <p className="text-[#555] mt-0.5">{award.location} · {award.dates}</p>
                                    <p className="text-[#666] mt-1 leading-relaxed">{award.description}</p>

                                    {/* Hover reveal */}
                                    <div className="mt-2 flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <span className="text-[9px] font-mono text-primary/60">SCAN COMPLETE</span>
                                        <span className="text-[9px] font-mono text-[#444]">·</span>
                                        <span className="text-[9px] font-mono text-[#555]">Classification: VERIFIED</span>
                                        <span className="text-[9px] font-mono text-[#444]">·</span>
                                        <span className="text-[9px] font-mono text-primary/40">Achievement Unlocked</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </FadeSection>

            </div>
        </div>
    )
}
